import { useMemo, useState } from 'react';
import {
  FilterBuilder,
  SortableTh,
  TablePagination,
  applyFilterRules,
  useColumnResize,
  usePagination,
  useTableSort,
} from '@/components/ui/data-table';
import type {
  FilterColumn,
  FilterConjunction,
  FilterRule,
  SortAccessors,
} from '@/components/ui/data-table';
import type { LandingReadinessRow } from '@/services/landingPagesService';

/**
 * The readiness roster, on the same table toolkit as the rest of the app.
 *
 * ⚠ IT WAS A BARE <table> WITH NO SORT, NO SEARCH AND NO PAGING, which is
 * fine for the eight rows I tested it with and wrong for seventy. The useful
 * questions here are all orderings and subsets — "who is blocked on a form",
 * "which clients have no logo", "show me the ones I can build today" — and a
 * flat list answers none of them without scrolling and counting by eye.
 *
 * ⚠ AND THE BULK ACTION IS DELIBERATELY NARROW. Nothing here mutates a client:
 * the two things worth doing across a selection are copying the list (to paste
 * into a message to Comms about missing assets) and exporting it. A readiness
 * screen that could re-extract fifty logos on a click would be a button that
 * reads fifty live websites, and that belongs behind a script an operator runs
 * knowingly, not a checkbox column.
 */

const RESIZE_DEFAULTS: Record<string, number> = {
  client: 260,
  logo: 84,
  palette: 84,
  form: 84,
  ads: 84,
  gaps: 380,
};

const yn = (v: boolean) => (v ? 'Yes' : 'No');

const SORT_ACCESSORS: SortAccessors<LandingReadinessRow> = {
  client: (r) => (r.business_name || r.tenant_id).toLowerCase(),
  logo: (r) => (r.logo ? 1 : 0),
  palette: (r) => (r.palette ? 1 : 0),
  // Unchecked sorts between missing and present rather than pretending to be
  // either — the column has three states and so does its ordering.
  form: (r) => (!r.form_checked ? 1 : r.form ? 2 : 0),
  ads: (r) => (r.ads ? 1 : 0),
  gaps: (r) => r.defaulted.length,
};

const GAP_COPY: Record<string, string> = {
  logo: 'no usable logo',
  primary_color: 'no brand colour',
  secondary_color: 'no secondary colour',
  font_family: 'no brand typeface',
  phone: 'no phone',
  proof: 'no ratings',
  city: 'no city',
  state: 'no state',
  business_name: 'no business name',
  cta_text: 'no call to action',
};

function gapLabel(key: string): string {
  const [head, tail] = key.split(':', 2);
  if (head === 'trust_facts') {
    return `trust facts ${(tail || '').replace('_of_', ' of ')}`;
  }
  return GAP_COPY[head] || head.replace(/_/g, ' ');
}

function Cell({ ok, checked = true }: { ok: boolean; checked?: boolean }) {
  if (!checked) {
    return (
      <span className="text-muted-foreground" title="Not checked">
        –
      </span>
    );
  }
  return ok ? (
    <span className="text-[hsl(var(--status-success))]" aria-label="ready">
      ✓
    </span>
  ) : (
    <span className="text-destructive" aria-label="missing">
      ✗
    </span>
  );
}

export default function ReadinessTable({
  rows: allRows,
  tenantId,
}: {
  rows: LandingReadinessRow[];
  tenantId?: string;
}) {
  const [search, setSearch] = useState('');
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [conjunction, setConjunction] = useState<FilterConjunction>('and');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState('');

  const { sortKey, sortDir, toggleSort, sortRows } =
    useTableSort<LandingReadinessRow>('client', 'asc');
  const { widths, startResize } = useColumnResize(RESIZE_DEFAULTS);

  const filterColumns: FilterColumn<LandingReadinessRow>[] = useMemo(
    () => [
      {
        key: 'client',
        label: 'Client',
        type: 'text',
        accessor: (r) => `${r.business_name} ${r.tenant_id}`,
      },
      {
        key: 'logo',
        label: 'Logo',
        type: 'enum',
        accessor: (r) => yn(r.logo),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ],
      },
      {
        key: 'palette',
        label: 'Palette',
        type: 'enum',
        accessor: (r) => yn(r.palette),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ],
      },
      {
        key: 'form',
        label: 'Lead form',
        type: 'enum',
        accessor: (r) => (!r.form_checked ? 'Not checked' : yn(r.form)),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
          { value: 'Not checked', label: 'Not checked' },
        ],
      },
      {
        key: 'ads',
        label: 'Ads credential',
        type: 'enum',
        accessor: (r) => yn(r.ads),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ],
      },
      {
        key: 'gaps',
        label: 'Number of fallbacks',
        type: 'number',
        accessor: (r) => r.defaulted.length,
      },
      {
        key: 'falls_back_on',
        label: 'Falls back on',
        type: 'text',
        accessor: (r) => r.defaulted.map(gapLabel).join(' '),
      },
    ],
    [],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searched = q
      ? allRows.filter(
          (r) =>
            r.tenant_id.toLowerCase().includes(q) ||
            (r.business_name || '').toLowerCase().includes(q) ||
            r.defaulted.some((d) => gapLabel(d).includes(q)),
        )
      : allRows;
    const filtered = applyFilterRules(searched, rules, filterColumns, conjunction);
    return sortRows(filtered, SORT_ACCESSORS);
  }, [allRows, search, rules, filterColumns, conjunction, sortRows]);

  const { page, pageSize, setPage, setPageSize, pageCount, slice } = usePagination(
    rows.length,
    { defaultPageSize: 25 },
  );
  const pageRows = slice(rows);

  // ⚠ SELECTION SPANS FILTERING, so "select all" cannot mean "the page". It
  // selects what the current view actually contains, which is the set the
  // operator is looking at.
  const allShownSelected =
    rows.length > 0 && rows.every((r) => selected.has(r.tenant_id));
  const toggleAll = () =>
    setSelected(allShownSelected ? new Set() : new Set(rows.map((r) => r.tenant_id)));
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectedRows = allRows.filter((r) => selected.has(r.tenant_id));

  const copyList = async (withGaps: boolean) => {
    const text = selectedRows
      .map((r) =>
        withGaps
          ? `${r.tenant_id} — ${r.defaulted.map(gapLabel).join(', ') || 'nothing missing'}`
          : r.tenant_id,
      )
      .join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(`${selectedRows.length} copied`);
    window.setTimeout(() => setCopied(''), 2500);
  };

  const th = (key: string, label: string, className?: string) => (
    <SortableTh
      sortKey={key}
      currentKey={sortKey}
      dir={sortDir}
      onSort={toggleSort}
      resizable
      width={widths[key]}
      onResize={startResize}
      className={className}
    >
      {label}
    </SortableTh>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search client or gap…"
          aria-label="Search clients"
          className="w-64 rounded-md border border-border px-3 py-1.5 text-sm"
        />
        <FilterBuilder
          columns={filterColumns}
          rules={rules}
          onRulesChange={setRules}
          conjunction={conjunction}
          onConjunctionChange={setConjunction}
        />
        <span className="text-sm text-muted-foreground tabular-nums">
          {rows.length === allRows.length
            ? `${allRows.length} shown`
            : `${rows.length} of ${allRows.length} shown`}
        </span>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-coral bg-coral-light px-3 py-2 text-sm">
          <span className="font-medium tabular-nums">{selected.size} selected</span>
          <button
            type="button"
            onClick={() => copyList(false)}
            className="rounded-md border border-border bg-background px-2 py-1 hover:bg-teal-light"
          >
            Copy domains
          </button>
          <button
            type="button"
            onClick={() => copyList(true)}
            className="rounded-md border border-border bg-background px-2 py-1 hover:bg-teal-light"
          >
            Copy with gaps
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
          {copied && <span className="text-teal-dark">{copied}</span>}
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[56rem] text-sm">
          <thead className="bg-teal-light text-left">
            <tr>
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  checked={allShownSelected}
                  onChange={toggleAll}
                  aria-label="Select all shown"
                />
              </th>
              {th('client', 'Client')}
              {th('logo', 'Logo', 'text-center')}
              {th('palette', 'Palette', 'text-center')}
              {th('form', 'Form', 'text-center')}
              {th('ads', 'Ads', 'text-center')}
              {th('gaps', 'Falls back on')}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr
                key={r.tenant_id}
                className={`border-t border-border ${
                  r.tenant_id === tenantId ? 'bg-coral-light' : ''
                }`}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(r.tenant_id)}
                    onChange={() => toggleOne(r.tenant_id)}
                    aria-label={`Select ${r.tenant_id}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <span className="font-medium">{r.business_name || r.tenant_id}</span>
                  <div className="text-xs text-muted-foreground">{r.tenant_id}</div>
                  {r.error && <div className="text-xs text-destructive">{r.error}</div>}
                </td>
                <td className="px-3 py-2 text-center" title={r.logo_review || r.logo_note}>
                  <Cell ok={r.logo} />
                  {r.logo_note.includes('light plate') && (
                    <div className="text-[10px] leading-tight text-muted-foreground">
                      plated
                    </div>
                  )}
                  {/* ⚠ THE FLAG THAT HAD NOWHERE TO GO. mark_suspicion records
                      "this may be a badge, not their logo" on the extraction,
                      and it lived only in the database — the sweep printed it
                      once and nothing ever asked again. It reads as a pass
                      here otherwise, which is the worst of both: a doubt
                      recorded and a tick displayed. Overdue is called out
                      separately, because a flag with no due date is
                      indistinguishable from a decision nobody has to make. */}
                  {r.logo_review && (
                    <div
                      className={
                        r.logo_review_overdue
                          ? 'text-[10px] font-medium leading-tight text-destructive'
                          : 'text-[10px] leading-tight text-muted-foreground'
                      }
                    >
                      {r.logo_review_overdue
                        ? `check — ${r.logo_review_days}d`
                        : 'check'}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  <Cell ok={r.palette} />
                </td>
                <td
                  className="px-3 py-2 text-center"
                  title={r.form_checked ? r.form_reason : 'Not checked'}
                >
                  <Cell ok={r.form} checked={r.form_checked} />
                </td>
                <td className="px-3 py-2 text-center" title={r.ads_reason}>
                  <Cell ok={r.ads} />
                </td>
                <td className="px-3 py-2">
                  {r.defaulted.length === 0 ? (
                    <span className="text-xs text-muted-foreground">nothing</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {r.defaulted.map((d) => (
                        <span
                          key={d}
                          className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                        >
                          {gapLabel(d)}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No client matches this search or filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        totalRows={rows.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
